import { ProductType } from '@allesauto/graphql';
import { ToastifyType } from '@allesauto/utils/consts';
import { ErrorResponse } from '@apollo/client/link/error';
import isArray from 'lodash/isArray';

interface OperationCustomError {
  type?: ToastifyType.SUCCESS | ToastifyType.ERROR | ToastifyType.WARNING;
  headerKey?: string;
  textKey?: string;
  condition?: (errorResponse: ErrorResponse) => boolean;
}

interface ApolloErrorHandlerConfig {
  bypass?: string[];
  customErrors?: {
    [key: string]: OperationCustomError | OperationCustomError[];
  };
}

const CartCheckoutErrors = [
  {
    type: ToastifyType.WARNING as ToastifyType.WARNING,
    headerKey: 'global.ERRORS.VEHICLE_NOT_AVAILABLE.HEADER',
    textKey: 'global.ERRORS.VEHICLE_NOT_AVAILABLE.TEXT',
    condition: (errorResponse: ErrorResponse) =>
      hasGraphQLCodes(errorResponse, ['102', '113']) &&
      isAddToCartOfType(errorResponse, ProductType.Vehicle),
  },
  {
    type: ToastifyType.WARNING as ToastifyType.WARNING,
    headerKey: 'global.ERRORS.ACCESSORY_NOT_AVAILABLE.HEADER',
    textKey: 'global.ERRORS.ACCESSORY_NOT_AVAILABLE.TEXT',
    condition: (errorResponse: ErrorResponse) =>
      hasGraphQLCodes(errorResponse, ['113']) &&
      isAddToCartOfType(errorResponse, ProductType.Accessory),
  },
  {
    type: ToastifyType.WARNING as ToastifyType.WARNING,
    headerKey: 'global.ERRORS.SERVICE_NOT_AVAILABLE.HEADER',
    textKey: 'global.ERRORS.SERVICE_NOT_AVAILABLE.TEXT',
    condition: (errorResponse: ErrorResponse) =>
      hasGraphQLCodes(errorResponse, ['113']) &&
      isAddToCartOfType(errorResponse, ProductType.Service),
  },
  {
    type: ToastifyType.ERROR as ToastifyType.ERROR,
    headerKey: 'global.ERRORS.TRADE_IN_SERVICE_NOT_AVAILABLE.HEADER',
    textKey: 'global.ERRORS.TRADE_IN_SERVICE_NOT_AVAILABLE.TEXT',
    condition: (errorResponse: ErrorResponse) =>
      isAddToCartOfType(errorResponse, ProductType.TradeInVehicle),
  },
  // { // todo: was commented untill BE implements a real code system on their side, rn these codes are not reliable
  //   type: ToastifyType.WARNING as ToastifyType.WARNING,
  //   headerKey: 'global.ERRORS.SHOPPING_CART_CHANGED.HEADER',
  //   textKey: 'global.ERRORS.SHOPPING_CART_CHANGED.TEXT',
  //   condition: (errorResponse: ErrorResponse) =>
  //     hasGraphQLCodes(errorResponse, ['118']),
  // },
];

const GarageVehicleErrors = [
  {
    type: ToastifyType.ERROR as ToastifyType.ERROR,
    headerKey: 'global.ERRORS.DELETE_GARAGE_VEHICLE.HEADER',
    textKey: 'global.ERRORS.DELETE_GARAGE_VEHICLE.TEXT',
  },
  {
    type: ToastifyType.ERROR as ToastifyType.ERROR,
    headerKey: 'global.ERRORS.GARAGE_UPDATE_VEHICLE.HEADER',
    textKey: 'global.ERRORS.GARAGE_UPDATE_VEHICLE.TEXT',
  },
];

const CalculateErrors = [
  {
    type: ToastifyType.ERROR as ToastifyType.ERROR,
    headerKey: 'global.ERRORS.CALCULATE_LOAN.HEADER',
    textKey: 'global.ERRORS.CALCULATE_LOAN.TEXT',
  },
  {
    type: ToastifyType.ERROR as ToastifyType.ERROR,
    headerKey: 'global.ERRORS.CALCULATE_LEASING.HEADER',
    textKey: 'global.ERRORS.CALCULATE_LEASING.TEXT',
  },
];

const C2CVehicleErrors = {
  type: ToastifyType.ERROR as ToastifyType.ERROR,
  headerKey: 'global.ERRORS.DELETE_C2C_VEHICLE.HEADER',
  textKey: 'global.ERRORS.DELETE_C2C_VEHICLE.TEXT',
};

const WorkshopServiceErrors = {
  type: ToastifyType.ERROR as ToastifyType.ERROR,
  headerKey: 'global.ERRORS.BOOK_WORKSHOP_SERVICES.HEADER',
  textKey: 'global.ERRORS.BOOK_WORKSHOP_SERVICES.TEXT',
};

const TradeInVariationValuationErrors = {
  type: ToastifyType.ERROR as ToastifyType.ERROR,
  headerKey: 'global.ERRORS.TRADE_IN_VARIATION_VALUATION.HEADER',
  textKey: 'global.ERRORS.TRADE_IN_VARIATION_VALUATION.TEXT',
};

/**
 * @param bypass List of Apollo operations (names) which should not be handled by the handler
 * @param customErrors Custom toasts for Apollo operations, an object where each key is an Apollo operation name
 * @param customErrors.OperationName Apollo operation name for which you need a custom toast, its value could be an object or an array of objects
 * @param customErrors.OperationName.type Type of a toast
 * @param customErrors.OperationName.headerKey Message key from '@allesauto/i18n' for a toast header
 * @param customErrors.OperationName.textKey Message key from '@allesauto/i18n' for a toast text
 * @param customErrors.OperationName.condition - Function returning a boolean value, that defines if the custom message should be shown
 * @example
 * {
 *   bypass: ['UpdateCustomerPassword', 'CreateCustomer'],
 *   customErrors: {
 *     CalculateLeasing: {
 *       type: ToastifyType.WARNING,
 *       headerKey: 'global.ERRORS.LEASING.HEADER',
 *       textKey: 'global.ERRORS.LEASING.TEXT',
 *     },
 *     CalculateLoan: {
 *       textKey: 'global.ERRORS.LOAN.TEXT',
 *     },
 *   },
 * }
 */
const config: ApolloErrorHandlerConfig = {
  bypass: [
    'UpdateCustomerPassword',
    'CreateCustomer',
    'createCustomerAccessToken',
    'tradeInVariationValuation',
  ],
  customErrors: {
    AddToCart: CartCheckoutErrors,
    DeleteFromCart: CartCheckoutErrors,
    DeleteGarageVehicle: GarageVehicleErrors[0],
    DeleteC2cVehicle: C2CVehicleErrors,
    UpdateGarageVehicle: GarageVehicleErrors[1],
    AddGarageVehicle: GarageVehicleErrors[1],
    BookWorkshopService: WorkshopServiceErrors,
    CalculateLoan: CalculateErrors[0],
    CalculateLeasing: CalculateErrors[1],
    TradeInVariationValuation: TradeInVariationValuationErrors,
  },
};

// It checks if the response contains specific error codes
// Known GQL error codes - https://github.com/spryker/carts-rest-api/blob/master/src/Spryker/Glue/CartsRestApi/CartsRestApiConfig.php
function hasGraphQLCodes({ response }: ErrorResponse, codes: string[]) {
  if (response?.errors?.[0]) {
    const internalErrors = response.errors[0].extensions?.errors;

    if (internalErrors && isArray(internalErrors)) {
      return codes.every((c) => internalErrors.find((e) => e.code === c));
    }
  }

  return false;
}

// It checks if the request is used to add ONE product of a certain type to the cart
function isAddToCartOfType(
  { operation }: ErrorResponse,
  productType: ProductType
) {
  const items = operation.variables?.input?.items;

  return (
    items &&
    isArray(items) &&
    items.length === 1 &&
    items[0].productType === productType
  );
}

export default config;
